import type { RootState } from 'src/redux/store';
import type { BoxProps } from '@mui/material/Box';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { usePremissionContext } from 'src/pages/auth/hooks';
import { hasBillingViewPermission } from 'src/pages/auth/hooks/permission-utils';

import { Iconify } from 'src/components/iconify';

import { formatSARCurrency } from './balance-display-refactored/utils';
import { countUp, balanceUpdateGlow } from './balance-display-refactored/animations';
import { BalanceCompact } from './balance-display-refactored/components/balance-compact';
import { useBalanceManager } from './balance-display-refactored/hooks/use-balance-manager';
import { CurrencySymbol } from '../../pages/setting/components/billing/utils/currency-utils';
import {
  LoadingText,
  PulseAnimation,
  ShakeAnimation,
} from './balance-display-refactored/components/shared-components';

// ----------------------------------------------------------------------

interface BalanceData {
  balance: number;
  balance_accountant: number;
  balance_avail: number;
}

interface BalanceApiResponse {
  data?: {
    balance?: BalanceData;
  };
}

export interface BalanceDisplayProps extends BoxProps {
  variant?: 'default' | 'compact';
  isSuperAdmin?: boolean;
  noActivePlan?: boolean;
}

export function BalanceDisplay({
  variant = 'default',
  isSuperAdmin = false,
  noActivePlan = false,
  sx,
  ...other
}: BalanceDisplayProps) {
  const { t } = useTranslate('translation');
  const { permissions } = usePremissionContext();

  const userRole = useAppSelector((state: RootState) => {
    const data = state.auth.loginApiData;
    return data && 'role' in data ? (data as any).role : null;
  });
  const isUser = userRole?.toLowerCase() === 'user';
  const canShowBalance = isSuperAdmin || hasBillingViewPermission(permissions);
  const balanceFetchEnabled = !isUser && canShowBalance;

  const manager = useBalanceManager({ fetchEnabled: balanceFetchEnabled });

  // Read subscription currency from billing state
  const subscriptionCurrency = useAppSelector((state: RootState) => {
    const billing = (state as any).billing;
    return (
      billing?.activeSubscription?.plan?.currency ||
      billing?.activeSubscription?.plan?.currency_code ||
      'SAR'
    );
  });

  const { balanceData, isLoading } = manager;

  // Track balance changes to trigger animation
  const balanceInfo = (balanceData as BalanceApiResponse)?.data?.balance;
  const mainBalance = balanceInfo?.balance || 0;
  const accountantBalance = balanceInfo?.balance_accountant || 0;
  const availableBalance = balanceInfo?.balance_avail || 0;

  const isLowBalance = availableBalance < 10;
  const isCriticalBalance = availableBalance < 2;

  const [balanceUpdateAnimation, setBalanceUpdateAnimation] = useState(false);
  const [previousBalance, setPreviousBalance] = useState<number>(0);

  useEffect(() => {
    const currentBalance = balanceInfo?.balance || 0;
    if (previousBalance > 0 && currentBalance > previousBalance) {
      setBalanceUpdateAnimation(true);
      setTimeout(() => setBalanceUpdateAnimation(false), 2000);
    }
    setPreviousBalance(currentBalance);
  }, [balanceInfo?.balance, previousBalance]);

  if (isUser) {
    return null;
  }

  if (!canShowBalance) {
    return null;
  }

  if (isLoading) {
    return <LoadingText />;
  }

  const balance = {
    main: mainBalance,
    accountant: accountantBalance,
    available: availableBalance,
    isLow: isLowBalance,
    isCritical: isCriticalBalance,
  };

  if (variant === 'compact') {
    return (
      <BalanceCompact
        manager={manager}
        balance={balance}
        isLoading={isLoading}
        isSuperAdmin={isSuperAdmin}
        noActivePlan={noActivePlan}
        balanceUpdateAnimation={balanceUpdateAnimation}
        sx={sx}
        {...other}
      />
    );
  }

  // Default Variant
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('AVAILABLE_BALANCE_LABEL')}
        </Typography>
        <PulseAnimation loading={isLoading} lowBalance={isLowBalance}>
          <ShakeAnimation criticalBalance={isCriticalBalance}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color:
                  availableBalance > 0
                    ? isLowBalance
                      ? 'warning.main'
                      : 'success.main'
                    : 'error.main',
                animation: balanceUpdateAnimation
                  ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                  : 'none',
                transition: 'all 0.3s ease-in-out',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />
              {formatSARCurrency(availableBalance)}
            </Typography>
          </ShakeAnimation>
        </PulseAnimation>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('MAIN_BALANCE_LABEL')}
        </Typography>
        <PulseAnimation loading={isLoading} lowBalance={false}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: mainBalance > 0 ? 'info.main' : 'text.secondary',
              animation: balanceUpdateAnimation
                ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                : 'none',
              transition: 'all 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />
            {formatSARCurrency(mainBalance)}
          </Typography>
        </PulseAnimation>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('ACCOUNTANT_LABEL')}
        </Typography>
        <PulseAnimation loading={isLoading} lowBalance={false}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: accountantBalance > 0 ? 'primary.main' : 'text.secondary',
              animation: balanceUpdateAnimation
                ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                : 'none',
              transition: 'all 0.3s ease-in-out',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} width={18} />
            {formatSARCurrency(accountantBalance)}
          </Typography>
        </PulseAnimation>
      </Box>
    </Box>
  );
}
